import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, QueryStatus } from "@cx/ui";
import type { NfeVoidOverview } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { EventsTimeline, VoidRangeDialog } from "./InvoiceEvents";
import { ENVIRONMENT_LABEL, eventInFlight } from "./labels";

/**
 * Preferências › Fiscal › NF-e › Inutilização (T2.4): lacunas da numeração da série (números pulados e notas
 * rejeitadas abandonadas) oferecidas para inutilizar, faixa livre e histórico das inutilizações com XML/PDF.
 */
export function VoidRangePage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const [series, setSeries] = useState<number | null>(null);
  const [range, setRange] = useState<{ from?: number; to?: number } | null>(null);
  const q = useQuery({
    queryKey: ["nfe-void", companyId, series],
    queryFn: () => api<NfeVoidOverview>(`/fiscal-documents/void-ranges${series ? `?series=${series}` : ""}`),
    refetchInterval: (query) => (query.state.data?.events.some(eventInFlight) ? 2500 : false),
  });
  const o = q.data;
  const canUpdate = can(perms, "invoices", "update");
  const reload = () => void qc.invalidateQueries({ queryKey: ["nfe-void", companyId] });
  return (
    <>
      <ContentHeader
        title="Inutilização de numeração"
        breadcrumb={["Preferências", "Fiscal", "Nota fiscal eletrônica", "Inutilização"]}
        actions={
          <>
            <Link to="/preferencias/fiscal/nfe" className="btn btn-outline-secondary">
              Voltar
            </Link>
            {o && canUpdate && (
              <button type="button" className="btn btn-primary" onClick={() => setRange({})}>
                Inutilizar faixa
              </button>
            )}
          </>
        }
      />
      <QueryStatus loading={q.isLoading} error={q.error} />
      {o && (
        <>
          <section className="box card mb-3" aria-labelledby="void-gaps">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                <h2 id="void-gaps" className="h5 mb-0 me-auto">
                  Lacunas da série {o.series}
                </h2>
                <label className="small">
                  Série{" "}
                  <input
                    className="form-control form-control-sm d-inline-block"
                    style={{ width: "6rem" }}
                    inputMode="numeric"
                    aria-label="Série"
                    defaultValue={o.series}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (Number.isInteger(n) && n >= 0 && n <= 999) setSeries(n);
                    }}
                  />
                </label>
              </div>
              <p className="text-muted small">
                Ambiente: {ENVIRONMENT_LABEL[o.environment]}. Próximo número da série: {o.nextNumber}. Números
                que ficaram para trás (pulados ou de notas rejeitadas que não serão reenviadas) devem ser
                inutilizados na SEFAZ.
              </p>
              {o.gaps.length === 0 ? (
                <p className="mb-0" data-testid="no-gaps">
                  Nenhuma lacuna na numeração.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0" aria-label="Lacunas da numeração">
                    <thead>
                      <tr>
                        <th scope="col">Números</th>
                        <th scope="col">Motivo</th>
                        <th scope="col" className="text-end">
                          Ação
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.gaps.map((g) => (
                        <tr key={`${g.from}-${g.to}`}>
                          <td>{g.from === g.to ? g.from : `${g.from} a ${g.to}`}</td>
                          <td>
                            {g.kind === "rejected" ? (
                              <>
                                NF-e rejeitada{" "}
                                {g.documentIds.map((id) => (
                                  <Link
                                    key={id}
                                    to="/vendas/notas-fiscais/$id"
                                    params={{ id }}
                                    className="ms-1"
                                  >
                                    abrir
                                  </Link>
                                ))}
                              </>
                            ) : (
                              "Número não usado"
                            )}
                          </td>
                          <td className="text-end">
                            {canUpdate && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setRange({ from: g.from, to: g.to })}
                              >
                                Inutilizar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
          <section className="box card mb-3" aria-labelledby="void-history">
            <div className="card-body">
              <h2 id="void-history" className="h5">
                Inutilizações
              </h2>
              {o.events.length === 0 ? (
                <p className="mb-0 text-muted">Nenhuma inutilização pedida.</p>
              ) : (
                <EventsTimeline events={o.events} canUpdate={canUpdate} onChanged={reload} />
              )}
            </div>
          </section>
          <VoidRangeDialog
            open={range !== null}
            series={o.series}
            from={range?.from}
            to={range?.to}
            onClose={() => setRange(null)}
            onDone={() => {
              setRange(null);
              reload();
            }}
          />
        </>
      )}
    </>
  );
}
